import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { FieldGroupModel } from 'src/shared/models/field/field-group';
import { FieldModel, FieldType } from 'src/shared/models/field/field';
import { UserFieldResponse } from 'src/shared/models/field/user-field-response';
import { TabsProvider } from 'src/providers/tabs/tabs.provider';
import { IProperty } from 'src/constants/global-interfaces';
import * as fp from "lodash/fp";

export interface SetupableFormModel {
  antraegeName: string
  fields: IProperty<any>[]
  defaultRadioValue?: string
}

@Component({
  selector: 'rota-setupable-form',
  styleUrl: 'setupable-form.scss',
  templateUrl: 'setupable-form.html',
  standalone: false,
})
export class SetupableFormComponent implements OnInit, OnDestroy {
  @Input() fieldGroups$: Observable<Array<FieldGroupModel>>;

  @Input() requestModel: SetupableFormModel;

  form = this.fb.group({}); // Both used for rendering
  fieldGroups = new Array<FieldGroupModel>(); // Metadata for form

  private fields = new Array<FieldModel>();
  private fieldsSubscription: Subscription;
  private selectedOptions = new Array<string>();

  @Output() cancel = new EventEmitter<void>();
  @Output() customSubmit = new EventEmitter<Array<UserFieldResponse>>();

  get isEmpty() {
    return !this.fieldGroups
      .map(f => (f.fields || []).length)
      .reduce((acc, cur) => acc + cur, 0);
  }

  get dependentFieldsPerOptionId() {
    const dependentFieldsPerOptionId = this.fieldGroups
      .filter(fg => fg.optionId)
      .reduce((acc: any, cur: any) => ({ ...acc, [cur.optionId!]: (acc[cur.optionId!] || 0 + cur.fields.length) }), {});
    return Object.keys(dependentFieldsPerOptionId).length ? dependentFieldsPerOptionId : null;
  }
  get visibleFieldGroups() {
    return this.fieldGroups.filter(fg => !fg.optionId || this.selectedOptions.some(so => so === fg.optionId));
  }

  constructor(
    private fb: FormBuilder,
    private tabsProvider: TabsProvider,
  ) { }

  async ngOnInit(): Promise<void> {
    this.fieldsSubscription = this.fieldGroups$
      .subscribe(fgs => {
        fgs = fgs || [];
        this.fields = fgs.map(f => f.fields).reduce((acc, val) => acc.concat(val), []);
        this.form = this.fb.group(Object.assign(
          {},
          ...(this.fields || []).map(
            field => ({
              [field.id]: [field.defaultValue, field.validators],
            }))
        ));

        this.fieldGroups = fgs;

        if (!fp.get('antraegeName', this.requestModel)) return;

        const options = fp.get('[0].fields[0].options', fgs.filter(x => !x.optionId))
        if (options) {
          const field = options.find((x: any) => x.name === this.requestModel.antraegeName);

          if (field) this.requestModel.defaultRadioValue = field.id;
        }
        else return;

        if (!fp.first(this.requestModel.fields)) return;

        for (let i = 0; i < this.fieldGroups.length; i++) {
          const fieldGroup = this.fieldGroups[i];

          for (let j = 0; j < fieldGroup.fields.length; j++) {
            const field = fieldGroup.fields[j];

            for (let k = 0; k < this.requestModel.fields.length; k++) {
              const storageField = this.requestModel.fields[k];

              if (storageField['id'] === field.id) {
                field.value = storageField['value'];
              }
            }
          }
        }
      });
  }

  onSubmit(e: Event) {
    if (this.form.valid) {
      const visibleFields = this.visibleFieldGroups
        .map(f => f.fields)
        .reduce((acc, val) => acc.concat(val), []);
      const res = UserFieldResponse.fromFormValues(this.form.value);
      const mapped = res
        .filter(resp => visibleFields.some(vf => vf.id === resp.fieldId))
        .map((resp) => {
          const mappedRes = new UserFieldResponse(
            resp.fieldId,
            resp.response,
            { field: this.fields.find(f => f.id === resp.fieldId) }
          );
          if (this.isDateTimeField(mappedRes)) {
            // mappedRes.response = this.toAbsoluteDateTime(mappedRes.response);
            // const formattedDate = moment(mappedRes.response).format(mappedRes.meta?.field?.displayFormat);
            // mappedRes.response = formattedDate;
          }
          return mappedRes;
        }).sort((a, b) => a.meta!.field!.order - b.meta!.field!.order);
      this.customSubmit.emit(mapped);
    }
  }

  isDateTimeField({ fieldId, response }: UserFieldResponse) {
    return !!(this.fields.find((f: any) => f.id === fieldId)?.type === FieldType.DateTime && Date.parse(response));
  }

  handleOptionSelect(selectedOptions: Array<string>) {
    this.selectedOptions = selectedOptions || [];
  }

  goBack() {
    this.tabsProvider.forwardBack();
  }

  ngOnDestroy() {
    if (this.fieldsSubscription)
      this.fieldsSubscription.unsubscribe();
  }
}
